// /work-permit/$nationality — TH per-nationality detail
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { getWpNationality, WP_NATIONALITIES } from "@/data/work-permit-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Briefcase, ShieldCheck, Phone, MessageCircle, ArrowRight, Sparkles, Clock, Wallet, Building2, FileText, Languages } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/work-permit/$nationality")({
  loader: ({ params }) => {
    const n = getWpNationality(params.nationality);
    if (!n) throw notFound();
    return { slug: params.nationality };
  },
  head: ({ params }) => {
    const n = getWpNationality(params.nationality);
    if (!n) return { meta: [{ title: ctaTitle("ไม่พบสัญชาติ", "/work-permit/$nationality", "th")}] };
    const url = `${SITE}/work-permit/${n.slug}`;
    const title = `Work Permit คน${n.nameTh} ${n.flag} · Non-B + BOI ฟาสต์แทร็ก ${n.fastTrackDays} วัน | NYC`;
    const desc = `บริการ Work Permit + Non-B Visa สำหรับชาว${n.nameTh} (${n.countryEn}) — เงินเดือนขั้นต่ำ · BOI/EEC ${n.fastTrackDays} วัน · MOL ปกติ ${n.standardDays} วัน · รวมแปล + MFA + ครอบครัว Dependent 99.6% ผ่าน — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    return {
      meta: [
        { title: ctaTitle(title, "/work-permit/$nationality", "th") }, { name: "description", content: ctaDesc(desc, "/work-permit/$nationality", "th")},
        { name: "keywords", content: n.keywordsTh.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/work-permit/$nationality", "th")}, { property: "og:description", content: ctaDesc(desc, "/work-permit/$nationality", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/work-permit/${n.slug}`, `/en/work-permit/${n.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `Thailand Work Permit + Non-B Visa สำหรับชาว${n.nameTh}`,
              serviceType: "Work Permit Processing",
              description: desc,
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: { "@type": "Country", name: "Thailand" },
              audience: { "@type": "Audience", audienceType: n.demonymEn },
              offers: { "@type": "Offer", price: "12000", priceCurrency: "THB", description: "Work Permit + Non-B + ครอบครัว Dependent" },
            }),
          },
          howToSchema({
            name: `วิธีขอ Work Permit สำหรับคน${n.nameTh}`,
            description: `ขั้นตอนเฉพาะสำหรับชาว${n.countryEn}`,
            totalTime: `P${n.standardDays}D`,
            estimatedCost: { currency: "THB", value: "12000" },
            inLanguage: "th",
            steps: [
              { name: `รวบรวมเอกสารต้นทาง (${n.countryEn})`, text: `ปริญญา, ใบรับรองงาน, สมรส, สูติบัตร — รับรองที่ ${n.consulates[0]}` },
              { name: "แปลโดยนักแปลขึ้นทะเบียน", text: `แปลภาษา → ไทย (และ ${n.demonymEn} → English ถ้าจำเป็น) โดยนักแปล MOJ + รับรอง MFA ไทย`, url: `${SITE}/translate` },
              { name: "ขอ Non-B Visa", text: `ยื่นที่ ${n.consulates[0]} ใช้ 5-7 วันทำการ` },
              { name: "เข้าไทย + ยื่น Work Permit", text: `${n.fastTrackDays} วัน (BOI/EEC) หรือ ${n.standardDays} วัน (MOL) — เงินเดือนขั้นต่ำ ฿${n.minSalaryTHB.toLocaleString()}/เดือน` },
              { name: "Dependent + Re-Entry", text: "ขอ Non-B Dependent ให้คู่สมรส/บุตร + Multiple Re-Entry 3,800 บาท" },
            ],
          }),
          faqPageSchema([
            { q: `คน${n.nameTh}ขอ Work Permit นานแค่ไหน?`, a: `BOI/EEC ฟาสต์แทร็ก ${n.fastTrackDays} วัน · MOL ปกติ ${n.standardDays} วัน` },
            { q: `เงินเดือนขั้นต่ำสำหรับคน${n.nameTh}คือเท่าไหร่?`, a: `${n.minSalaryTHB.toLocaleString()} บาท/เดือน ตามเกณฑ์กระทรวงแรงงาน` },
            { q: `อุตสาหกรรมไหนรับคน${n.nameTh}มากที่สุด?`, a: n.topIndustries.join(" · ") },
            { q: `ต้องยื่นวีซ่าที่สถานทูตไหน?`, a: n.consulates.join(" · ") },
            { q: `เอกสารต้องรับรองอย่างไร?`, a: n.noteTh },
          ]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "Work Permit", item: `${SITE}/work-permit` },
                { "@type": "ListItem", position: 3, name: `คน${n.nameTh}`, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: WpDetail,
});

function WpDetail() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getWpNationality(slug)!;
  const others = WP_NATIONALITIES.filter((x) => x.slug !== slug && x.region === n.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-14 pb-10">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link><span className="mx-2">/</span>
            <Link to="/work-permit" className="hover:text-primary">Work Permit</Link><span className="mx-2">/</span>
            <span>คน{n.nameTh}</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Briefcase className="mr-1 inline h-3 w-3" /> Work Permit · {n.countryEn}
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">
            <span className="mr-3 text-4xl">{n.flag}</span>Work Permit สำหรับคน{n.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-muted-foreground sm:text-lg">{n.leadTh}</p>

          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">คน{n.nameTh}ขอ Work Permit ไทยใช้เวลาเท่าไหร่?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              คน{n.nameTh}ขอ Work Permit + Non-B Visa ไทยใช้เวลา {n.fastTrackDays} วัน (BOI/EEC ฟาสต์แทร็ก) หรือ {n.standardDays} วัน (MOL มาตรฐาน) เงินเดือนขั้นต่ำ {n.minSalaryTHB.toLocaleString()} บาท/เดือน · NYC ผ่าน 99.6%
            </p>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-4 max-w-3xl text-sm">
            <div className="rounded-lg border border-border bg-card p-2"><Clock className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">BOI</div><div className="font-bold">{n.fastTrackDays} วัน</div></div>
            <div className="rounded-lg border border-border bg-card p-2"><Clock className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">MOL</div><div className="font-bold">{n.standardDays} วัน</div></div>
            <div className="rounded-lg border border-border bg-card p-2"><Wallet className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">เงินเดือนขั้นต่ำ</div><div className="font-bold">฿{(n.minSalaryTHB / 1000).toFixed(0)}k</div></div>
            <div className="rounded-lg border border-border bg-card p-2"><Wallet className="h-4 w-4 text-primary" /><div className="text-[10px] text-muted-foreground">ค่าบริการ NYC</div><div className="font-bold">฿12,000</div></div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 space-y-12">
        <section>
          <h2 className="text-2xl font-extrabold"><Building2 className="mr-2 inline h-6 w-6 text-primary" />อุตสาหกรรมหลักที่รับคน{n.nameTh}</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {n.topIndustries.map((i) => (
              <li key={i} className="rounded-xl border border-border bg-card p-3 text-sm">• {i}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-extrabold"><FileText className="mr-2 inline h-6 w-6 text-primary" />สถานทูต/กงสุล สำหรับยื่น Non-B</h2>
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {n.consulates.map((c) => (
              <li key={c} className="rounded-xl border border-border bg-card p-3 text-sm">📍 {c}</li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-extrabold"><Languages className="mr-2 inline h-6 w-6 text-primary" />ข้อมูลเฉพาะสำหรับคน{n.nameTh}</h2>
          <p className="mt-4 rounded-2xl border-l-4 border-primary bg-primary/5 p-4 text-sm leading-relaxed">{n.noteTh}</p>
        </section>

        <section>
          <h2 className="text-2xl font-extrabold">ข้อมูลสำคัญแบบสรุป (Key Facts)</h2>
          <table className="mt-4 w-full text-sm">
            <tbody className="divide-y divide-border">
              {[
                ["สัญชาติ", `${n.flag} ${n.countryEn} (${n.demonymEn})`],
                ["BOI/EEC Fast-Track", `${n.fastTrackDays} วันทำการ`],
                ["MOL Standard", `${n.standardDays} วันทำการ`],
                ["เงินเดือนขั้นต่ำ", `${n.minSalaryTHB.toLocaleString()} บาท/เดือน`],
                ["ค่าธรรมเนียมรัฐ", "3,000 บาท/ปี"],
                ["ค่าบริการ NYC", "12,000 บาท (รวมยื่นแทน + แปล + Dependent)"],
                ["Dependent Visa", "คู่สมรส + บุตร <20 ปี (Non-B Dependent ฟรี)"],
                ["Re-Entry Permit", "Multiple 3,800 บาท"],
              ].map(([k, v]) => (
                <tr key={k}><td className="py-2 pr-4 font-semibold">{k}</td><td className="py-2">{v}</td></tr>
              ))}
            </tbody>
          </table>
        </section>

        <section>
          <h2 className="text-2xl font-extrabold"><ShieldCheck className="mr-2 inline h-6 w-6 text-primary" />บริการเสริมที่มักใช้คู่กัน</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Link to="/visa/$nationality" params={{ nationality: n.slug }} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">วีซ่าไทยทุกประเภทสำหรับคน{n.nameTh}</div>
              <div className="mt-1 text-xs text-muted-foreground">Non-B / LTR / DTV / Smart / Elite / Retirement / Marriage</div>
            </Link>
            <Link to="/industrial-estates" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">บริการในนิคมอุตสาหกรรม 20+ แห่ง</div>
              <div className="mt-1 text-xs text-muted-foreground">EEC / Amata / WHA / Lat Krabang / Rojana</div>
            </Link>
            <Link to="/translate" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">แปลเอกสาร + รับรอง MFA</div>
              <div className="mt-1 text-xs text-muted-foreground">ISO 17100 · 800 บาท/หน้า</div>
            </Link>
            <Link to="/apostille" className="rounded-xl border border-border bg-card p-4 hover:border-primary">
              <div className="font-bold">Apostille (Hague)</div>
              <div className="mt-1 text-xs text-muted-foreground">60+ ประเทศคู่สัญญา</div>
            </Link>
          </div>
        </section>

        {others.length > 0 && (
          <section className="border-t border-border pt-10">
            <h2 className="text-2xl font-extrabold">สัญชาติใกล้เคียงในภูมิภาคเดียวกัน</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((o) => (
                <Link key={o.slug} to="/work-permit/$nationality" params={{ nationality: o.slug }} className="group rounded-2xl border border-border bg-card p-4 hover:border-primary">
                  <div className="flex items-center gap-2">
                    <div className="text-2xl">{o.flag}</div>
                    <div className="font-bold">คน{o.nameTh}</div>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">BOI {o.fastTrackDays}d · MOL {o.standardDays}d</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">ดู Work Permit <ArrowRight className="h-3 w-3" /></div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
